/**
 * LiveRackGrid Component
 * 
 * Live visualization of rack locations with real-time activity animations.
 * Extends RackGrid with activity highlighting, pulsing effects, and smooth transitions.
 * 
 * Features:
 * - Real-time activity highlighting per location
 * - Pulsating animations (5 seconds) with gradual fade-out (2 seconds)
 * - Color-coded activities (Pick=Orange, PutAway=Green, Transfer=Blue, etc.)
 * - Support for multiple simultaneous activities
 * - Activity quantity and type indicators
 * - Smooth rack transitions
 * - Automatic scrolling to active locations (including after rack switch)
 * 
 * @author BizClick WMS
 * @version 1.28 - Improved rack switch scrolling with targetBinCode
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Package, AlertTriangle, ArrowUpDown, ArrowDownToLine, ArrowUpFromLine, Hash, ClipboardCheck, PackageOpen, Clock } from 'lucide-react';
import { BinLocation, RackStructure, compareSegments } from '../rack-builder/types';
import { LocationActivityEvent, ActivityType, ActivityTypeNames } from '../../../../types/locationActivity';
import { useTranslation } from '../../../../contexts/I18nProvider';
import '../../../../styles/location-activity-animations.css';

interface LiveRackGridProps {
  /** Rack structure to display */
  rackStructure: RackStructure;
  
  /** Active location activities (BinCode -> Events) */
  activeActivities: Map<string, LocationActivityEvent[]>;
  
  /** Optional: Target BinCode to scroll to (useful for rack switches) */
  targetBinCode?: string | null;
  
  /** Optional: Enable rack transition animation */
  enableTransition?: boolean;
  
  /** Optional: Animation speed ('slow' | 'normal' | 'fast') */
  animationSpeed?: 'slow' | 'normal' | 'fast';
  
  /** Optional: Enable automatic scrolling to active locations */
  enableAutoScroll?: boolean;
  
  /** Optional: Callback when activity animation completes */
  onActivityComplete?: (binCode: string, event: LocationActivityEvent) => void;
}

interface CellPosition {
  vertical: string;
  shelf: string;
}

/**
 * Activity animation state for a single location
 */
interface ActivityAnimationState {
  event: LocationActivityEvent;
  startTime: number;
  phase: 'pulse' | 'fadeout' | 'complete';
}

/**
 * Map ActivityType enum to CSS class name
 */
const getActivityClassName = (activityType: ActivityType): string => {
  switch (activityType) {
    case ActivityType.Pick:
      return 'activity-pick';
    case ActivityType.PutAway:
      return 'activity-putaway';
    case ActivityType.Transfer:
      return 'activity-transfer';
    case ActivityType.CorrectionOut:
      return 'activity-correctionout';
    case ActivityType.CorrectionIn:
      return 'activity-correctionin';
    case ActivityType.Counting:
      return 'activity-counting';
    case ActivityType.Receiving:
      return 'activity-receiving';
    case ActivityType.Reservation:
      return 'activity-reservation';
    default:
      return 'activity-transfer'; // Fallback
  }
};

/**
 * Get icon for activity type
 */
const getActivityIcon = (activityType: ActivityType) => {
  switch (activityType) {
    case ActivityType.Pick:
      return ArrowUpFromLine;
    case ActivityType.PutAway:
      return ArrowDownToLine;
    case ActivityType.Transfer:
      return ArrowUpDown;
    case ActivityType.CorrectionOut:
      return ArrowUpFromLine;
    case ActivityType.CorrectionIn:
      return ArrowDownToLine;
    case ActivityType.Counting:
      return ClipboardCheck;
    case ActivityType.Receiving:
      return PackageOpen;
    case ActivityType.Reservation:
      return Clock;
    default:
      return Hash;
  }
};

/**
 * Get animation duration based on speed setting
 */
const getAnimationDuration = (speed: 'slow' | 'normal' | 'fast'): { pulse: number; fadeout: number } => {
  switch (speed) {
    case 'slow':
      return { pulse: 7000, fadeout: 3000 };
    case 'fast':
      return { pulse: 3000, fadeout: 1500 };
    case 'normal':
    default:
      return { pulse: 5000, fadeout: 2000 };
  }
};

export const LiveRackGrid: React.FC<LiveRackGridProps> = ({
  rackStructure,
  activeActivities,
  targetBinCode = null,
  enableTransition = true,
  animationSpeed = 'normal',
  enableAutoScroll = true,
  onActivityComplete
}) => {
  const { t } = useTranslation();
  // Animation state tracking
  const [animationStates, setAnimationStates] = useState<Map<string, ActivityAnimationState[]>>(new Map());
  const gridRef = useRef<HTMLDivElement>(null);
  const animationTimers = useRef<Map<string, NodeJS.Timeout[]>>(new Map());
  const lastScrolledBinCode = useRef<string | null>(null);
  const pendingScrollTarget = useRef<string | null>(null);
  const scrollAttempts = useRef<number>(0);
  const previousRackNumber = useRef<string | null>(null);
  const isInitialMount = useRef(true);

  // Get animation durations
  const durations = useMemo(() => getAnimationDuration(animationSpeed), [animationSpeed]);

  // Create a map for quick lookup: "vertical-shelf" -> BinLocation
  const locationMap = useMemo(() => {
    const map = new Map<string, BinLocation>();
    rackStructure.locations.forEach((loc: BinLocation) => {
      const key = `${loc.vertical}-${loc.shelf}`;
      map.set(key, loc);
    });
    return map;
  }, [rackStructure.locations]);

  // Sort shelves in DESCENDING order (highest shelf at top, lowest at bottom)
  const sortedShelves = useMemo(
    () => [...rackStructure.shelves].sort((a, b) => compareSegments(b, a)),
    [rackStructure.shelves]
  );

  // Sort verticals in ascending order (left to right: V01, V02, V03, ...)
  const sortedVerticals = useMemo(
    () => [...rackStructure.verticals].sort(compareSegments),
    [rackStructure.verticals]
  );

  /**
   * Scroll to a specific location cell with retry mechanism
   */
  const scrollToLocation = useCallback((binCode: string, force: boolean = false, retry: boolean = true) => {
    if (!enableAutoScroll || !gridRef.current) {
      return false;
    }

    // Avoid scrolling to the same location multiple times rapidly (unless forced)
    if (!force && lastScrolledBinCode.current === binCode) {
      return false;
    }

    // Find the cell element by data attribute
    const cellElement = gridRef.current.querySelector(`[data-bincode="${binCode}"]`);
    
    if (cellElement) {
      lastScrolledBinCode.current = binCode;
      pendingScrollTarget.current = null;
      scrollAttempts.current = 0;
      
      console.log(`[LiveRackGrid] Scrolling to ${binCode}`);
      
      // Scroll with smooth behavior and center alignment
      cellElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });

      // Reset the last scrolled tracker after a delay to allow new scrolls
      setTimeout(() => {
        if (lastScrolledBinCode.current === binCode) {
          lastScrolledBinCode.current = null;
        }
      }, 2000);
      
      return true;
    } else {
      console.log(`[LiveRackGrid] Cell not found for ${binCode}, attempt ${scrollAttempts.current}`);
      
      // If retry enabled and we haven't tried too many times, set pending target
      if (retry && scrollAttempts.current < 10) {
        pendingScrollTarget.current = binCode;
        scrollAttempts.current++;
        return false;
      }
      
      return false;
    }
  }, [enableAutoScroll]);

  /**
   * Handle pending scroll attempts - retry when DOM is ready
   */
  useEffect(() => {
    if (pendingScrollTarget.current && scrollAttempts.current < 10) {
      const targetBin = pendingScrollTarget.current;
      const timeout = setTimeout(() => {
        const success = scrollToLocation(targetBin, true, true);
        if (!success && scrollAttempts.current < 10) {
          console.log(`[LiveRackGrid] Retry scroll to ${targetBin} in 100ms...`);
        } else if (success) {
          console.log(`[LiveRackGrid] Successfully scrolled to ${targetBin} after ${scrollAttempts.current} attempts`);
        } else {
          console.warn(`[LiveRackGrid] Failed to scroll to ${targetBin} after ${scrollAttempts.current} attempts`);
          pendingScrollTarget.current = null;
          scrollAttempts.current = 0;
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [pendingScrollTarget.current, scrollAttempts.current, rackStructure.locations, scrollToLocation]);

  /**
   * Handle target BinCode prop changes (for rack switches)
   */
  useEffect(() => {
    if (targetBinCode && enableAutoScroll) {
      console.log(`[LiveRackGrid] Target BinCode received: ${targetBinCode}`);
      
      // Check if rack has changed
      const hasRackChanged = previousRackNumber.current !== rackStructure.rackNumber;
      
      if (hasRackChanged) {
        console.log(`[LiveRackGrid] Rack changed from ${previousRackNumber.current} to ${rackStructure.rackNumber}`);
        previousRackNumber.current = rackStructure.rackNumber;
        
        // Set pending target and wait for DOM
        pendingScrollTarget.current = targetBinCode;
        scrollAttempts.current = 0;
        
        // Initial attempt after rack rendering delay
        setTimeout(() => {
          scrollToLocation(targetBinCode, true, true);
        }, 450);
      } else {
        // Same rack, scroll immediately
        setTimeout(() => {
          scrollToLocation(targetBinCode, false, true);
        }, 100);
      }
    }
  }, [targetBinCode, rackStructure.rackNumber, enableAutoScroll, scrollToLocation]);

  /**
   * Initialize rack tracking on mount
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousRackNumber.current = rackStructure.rackNumber;
    }
  }, []);

  /**
   * Handle new activities - start animations
   */
  useEffect(() => {
    const newStates = new Map<string, ActivityAnimationState[]>();
    let shouldScroll: string | null = null;
    let hasNewEvents = false;

    activeActivities.forEach((events, binCode) => {
      const existingStates = animationStates.get(binCode) || [];
      const newEvents = events.filter(
        event => !existingStates.some(state =>
          state.event.timestamp === event.timestamp &&
          state.event.activityType === event.activityType
        )
      );
      if (newEvents.length > 0) hasNewEvents = true;

      if (newEvents.length > 0) {
        const startTime = Date.now();
        const newStatesForLocation: ActivityAnimationState[] = [
          ...existingStates.filter(state => state.phase !== 'complete'),
          ...newEvents.map(event => ({
            event,
            startTime,
            phase: 'pulse' as const
          }))
        ];

        newStates.set(binCode, newStatesForLocation);

        // Mark this location for scrolling (use the first new activity)
        // But only if no targetBinCode is set (to avoid conflicts)
        if (!shouldScroll && !targetBinCode) {
          shouldScroll = binCode;
        }

        // Schedule phase transitions
        newEvents.forEach(event => {
          // Transition to fadeout after pulse duration
          const pulseTimer = setTimeout(() => {
            setAnimationStates(prev => {
              const updated = new Map(prev);
              const states = updated.get(binCode) || [];
              const stateIndex = states.findIndex(
                s => s.event.timestamp === event.timestamp && s.event.activityType === event.activityType
              );
              
              if (stateIndex !== -1) {
                states[stateIndex] = { ...states[stateIndex], phase: 'fadeout' };
                updated.set(binCode, [...states]);
              }
              
              return updated;
            });
          }, durations.pulse);

          // Mark as complete after fadeout
          const completeTimer = setTimeout(() => {
            setAnimationStates(prev => {
              const updated = new Map(prev);
              const states = updated.get(binCode) || [];
              const stateIndex = states.findIndex(
                s => s.event.timestamp === event.timestamp && s.event.activityType === event.activityType
              );
              
              if (stateIndex !== -1) {
                states[stateIndex] = { ...states[stateIndex], phase: 'complete' };
                
                // Notify completion
                if (onActivityComplete) {
                  onActivityComplete(binCode, event);
                }
                
                // Remove completed animations
                const filtered = states.filter(s => s.phase !== 'complete');
                if (filtered.length > 0) {
                  updated.set(binCode, filtered);
                } else {
                  updated.delete(binCode);
                }
              }
              
              return updated;
            });
          }, durations.pulse + durations.fadeout);

          // Store timers for cleanup
          const existingTimers: NodeJS.Timeout[] = animationTimers.current.get(binCode) || [];
          animationTimers.current.set(binCode, [...existingTimers, pulseTimer, completeTimer]);
        });
      } else if (existingStates.length > 0) {
        newStates.set(binCode, existingStates);
      }
    });

    // Clean up timers for removed activities
    animationStates.forEach((_, binCode) => {
      if (!activeActivities.has(binCode)) {
        const timers: NodeJS.Timeout[] = animationTimers.current.get(binCode) || [];
        timers.forEach((timer: NodeJS.Timeout) => clearTimeout(timer));
        animationTimers.current.delete(binCode);
      }
    });

    // Only update state when we actually added new animations or had stale
    // entries to clean up. Without this guard the effect re-runs on every
    // setAnimationStates (animationStates is in deps), causing an infinite loop.
    const hasStaleEntries = Array.from(animationStates.keys()).some(k => !activeActivities.has(k));
    if (hasNewEvents || hasStaleEntries) {
      setAnimationStates(newStates);
    }

    // Scroll to the location if we have a new activity (within same rack, and no explicit target)
    if (shouldScroll && !targetBinCode) {
      setTimeout(() => scrollToLocation(shouldScroll!, false, true), 100);
    }
  }, [activeActivities, animationStates, durations, onActivityComplete, scrollToLocation, targetBinCode]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      animationTimers.current.forEach((timers: NodeJS.Timeout[]) => {
        timers.forEach((timer: NodeJS.Timeout) => clearTimeout(timer));
      });
      animationTimers.current.clear();
    };
  }, []);

  /**
   * Get location by grid position
   */
  const getLocationAt = useCallback(
    (vertical: string, shelf: string): BinLocation | undefined => {
      const key = `${vertical}-${shelf}`;
      return locationMap.get(key);
    },
    [locationMap]
  );

  /**
   * Get cell class names with activity animations
   */
  const getCellClassName = (location: BinLocation | undefined): string => {
    if (!location) {
      return 'bg-gray-50 border border-dashed border-gray-200';
    }

    const animStates = animationStates.get(location.binCode);
    const baseClasses = 'relative border transition-colors select-none';

    // Check if location has active animation
    if (animStates && animStates.length > 0) {
      const primaryState = animStates[0]; // Use first (most recent) activity for primary styling
      const activityClass = getActivityClassName(primaryState.event.activityType);
      const animationClass = primaryState.phase === 'pulse' 
        ? 'activity-pulse' 
        : primaryState.phase === 'fadeout'
        ? 'activity-pulse-fadeout'
        : '';
      
      const multipleActivities = animStates.length > 1 ? 'multiple-activities' : '';

      return `${baseClasses} ${activityClass} ${animationClass} ${multipleActivities}`;
    }

    // Normal state (no activity)
    return `${baseClasses} bg-white border-gray-300`;
  };

  /**
   * Render activity indicators (badge and quantity)
   */
  const renderActivityIndicators = (location: BinLocation) => {
    const animStates = animationStates.get(location.binCode);
    
    if (!animStates || animStates.length === 0) {
      return null;
    }

    const primaryState = animStates[0];
    const ActivityIcon = getActivityIcon(primaryState.event.activityType);
    const activityName = ActivityTypeNames[primaryState.event.activityType];
    const totalQuantity = animStates.reduce((sum, state) => sum + state.event.quantity, 0);

    return (
      <>
        {/* Activity badge (type indicator) */}
        <div className="activity-badge">
          <ActivityIcon className="w-3 h-3" />
          <span>{activityName}</span>
          {animStates.length > 1 && (
            <span className="bg-blue-500 rounded-full px-1 text-[8px]">
              +{animStates.length - 1}
            </span>
          )}
        </div>

        {/* Quantity indicator */}
        {totalQuantity > 0 && (
          <div className="activity-quantity">
            {t('manager.modules.rackBuilderLive.rackGrid.quantityUnit', { '0': totalQuantity }, `${totalQuantity} stk`)}
          </div>
        )}
      </>
    );
  };

  if (rackStructure.locations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <p>{t('manager.modules.rackBuilderLive.rackGrid.noLocations', {}, 'Ingen lokasjoner å vise')}</p>
      </div>
    );
  }

  return (
    <div
      className={`w-full h-full flex flex-col ${enableTransition ? 'rack-fade-in' : ''}`}
    >
      {/* Grid container - fills all available space */}
      <div
        ref={gridRef}
        className="w-full h-full border border-gray-300 bg-white rounded-lg p-4 overflow-auto"
        style={{ userSelect: 'none' }}
      >
        {/* Header with vertical numbers - sticky */}
        <div className="flex mb-3 sticky top-0 bg-white z-10 border-b border-gray-200 pb-1">
          <div className="w-20 flex-shrink-0" /> {/* Spacer for shelf labels */}
          {sortedVerticals.map(vertical => (
            <div
              key={vertical}
              className="w-36 text-center font-semibold text-base text-gray-700 flex-shrink-0"
            >
              V{/^\d+$/.test(vertical) ? vertical.padStart(2, '0') : vertical}
            </div>
          ))}
        </div>

        {/* Rack visualization */}
        <div className="space-y-1.5">
          {sortedShelves.map(shelf => (
            <div key={shelf} className="flex items-center">
              {/* Shelf label - sticky */}
              <div className="w-20 text-right pr-4 font-semibold text-base text-gray-700 flex-shrink-0 sticky left-0 bg-white z-5">
                H{/^\d+$/.test(shelf) ? shelf.padStart(2, '0') : shelf}
              </div>

              {/* Cells for this shelf */}
              {sortedVerticals.map(vertical => {
                const location = getLocationAt(vertical, shelf);
                const cellClass = getCellClassName(location);

                return (
                  <div
                    key={`${vertical}-${shelf}`}
                    data-bincode={location?.binCode}
                    className={`w-36 h-30 ${cellClass} flex flex-col items-center justify-center p-2 text-sm flex-shrink-0`}
                  >
                    {location ? (
                      <>
                        {/* Activity indicators (overlays) */}
                        {renderActivityIndicators(location)}

                        {/* BinCode */}
                        <div className="font-mono font-semibold text-sm text-gray-800 relative z-10">
                          {location.binCode}
                        </div>

                        {/* Location indicators - fixed height */}
                        <div className="flex items-center justify-center space-x-1.5 mt-1.5 h-6 relative z-10">
                          {/* Dedicated location (has ItemNo) */}
                          {location.itemNo && (
                            <div title={t('manager.modules.rackBuilderLive.rackGrid.dedicatedLocation', {}, 'Dedikert lokasjon')}>
                              <Package className="w-4 h-4 text-green-600" />
                            </div>
                          )}

                          {/* Low priority indicator */}
                          {location.pickPriorityNo && location.pickPriorityNo > 50 && (
                            <div title={t('manager.modules.rackBuilderLive.rackGrid.lowPriority', {}, 'Lav prioritet')}>
                              <AlertTriangle className="w-4 h-4 text-yellow-600" />
                            </div>
                          )}
                        </div>

                        {/* Size/Type info */}
                        <div className="text-xs text-gray-500 mt-1 relative z-10">
                          {location.binSizeNo}/{location.binTypeNo}
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400 text-[8px]">-</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Compact legend */}
      <div className="mt-2 flex items-center space-x-4 text-xs">
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-orange-500/30 border border-orange-500/60 rounded" />
          <span>{t('manager.modules.rackBuilderLive.legend.pick', {}, 'Plukk')}</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-green-500/30 border border-green-500/60 rounded" />
          <span>{t('manager.modules.rackBuilderLive.legend.putAway', {}, 'Plassering')}</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-blue-500/30 border border-blue-500/60 rounded" />
          <span>{t('manager.modules.rackBuilderLive.legend.transfer', {}, 'Overføring')}</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-red-500/30 border border-red-500/60 rounded" />
          <span>{t('manager.modules.rackBuilderLive.legend.correctionOut', {}, 'Korreksjon Ut')}</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-purple-500/30 border border-purple-500/60 rounded" />
          <span>{t('manager.modules.rackBuilderLive.legend.counting', {}, 'Telling')}</span>
        </div>
        <div className="text-gray-500 ml-auto">
          {t('manager.modules.rackBuilderLive.rackGrid.liveActivity', {}, '⚡ Live aktivitet')}
        </div>
      </div>
    </div>
  );
};

export default LiveRackGrid;
